import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { rootState } from '../../../../../redux/store';
import { IActions, IWebSocketData, IWebSocketDataCommit } from '../../../../../interface/interfaces';
import { ExecButton } from '../../../../ui/ExecButton/ExecButton';
import { useSendWebsocketMessage } from '../../../WebSocket/hooks/useSendWebsocketMessage';
import { DataManagerSections, INITIAL_ACTION } from '../../../../../constants/constants';
import { HideShowSection } from '../../../../shared/HideShowSelections/HideShowSection';
import { trimString } from '../../../../../utils/trimString';
import { ToolTipWrapper } from '../../../../layout/ToolTipWrapper/ToolTipWrapper';
import { generateTimestamp } from '../../../../../utils/createTimestamp';
import { ModalContentWrapper } from '../../../../shared/Modal/ModalContentWrapper/ModalContentWrapper';
import { Input } from '../../../../shared/Input/Input';
import { Button } from '../../../../ui/Button';
import { FlexCenter } from '../../../../layout/FlexCenter/FlexCenter';
import { Spacing } from '../../../../layout/Spacing/Spacing';
import { Modal } from '../../../Modal/Modal';
import { closeModal, openModal } from '../../../Modal/modal.slice';

interface IDatamanagerFeaturesProps {
    socketRef: React.MutableRefObject<WebSocket | null>;
}

export const DatamanagerFeatures: React.FC<IDatamanagerFeaturesProps> = ({ socketRef }) => {
    const { inDepartment, outDepartment, rootDepartment } = useAppSelector((state: rootState) => state.arangeDepartments);
    const { dataAllChildrensPerNode } = useAppSelector((state: rootState) => state.filteredChildrenNodesPerSelection);
    const { project } = useAppSelector((state: rootState) => state.project);
    const { userAndTokenData } = useAppSelector((state: rootState) => state.loginUserAndTokenData);
    const {defaultProjectPath} = useAppSelector((state: rootState) => state.defaultProjectPath);

    const [selectedAction, setSelectedAction] = useState<IActions>(INITIAL_ACTION);

    const [inputField, setInputField] = useState('');

    const dispatch = useAppDispatch();

    const { sendWebsocketMessage } = useSendWebsocketMessage(socketRef);

    const onLaunchData = (action: IActions) => {
        setSelectedAction(action);

        const data = dataAllChildrensPerNode.map((item) => item.Id);

        const sendingWebsocketData: IWebSocketData = {
            defaultLocation: '',
            parameter: action.Parameter,
            data,
            department: rootDepartment.DepartmentName,
            projectID: project?._id || '',
            token: userAndTokenData?.token || '',
            projectPath: defaultProjectPath?.project_path || '',

        };

        const sendingWebsocketDataAndCommit: IWebSocketDataCommit = {
            ...sendingWebsocketData,
            timestamp: generateTimestamp(),
            // need to be dynamic
            commitText: inputField,
            sourceDepartmentID: rootDepartment.DepartmentId,
            targetDepartmentID: action.TargetDepartmentId,
            username: userAndTokenData?.user.username || 'default user',
        };

        // console.log('sendingWebsocketData: ', sendingWebsocketData);

        // only for commit actions
        if (action.Commiting) {
            // console.log(sendingWebsocketDataAndCommit);
            sendWebsocketMessage(JSON.stringify(sendingWebsocketDataAndCommit));
            setInputField('');
            dispatch(closeModal("commitModal"));
            return;
        }

        //all actions except commit
        sendWebsocketMessage(JSON.stringify(sendingWebsocketData));
    };

    const onSendingData = (action: IActions) => {
        setSelectedAction(action);
        dispatch(openModal("commitModal"));
    };

    const sendingData = rootDepartment.Actions.filter((item: IActions) => item.GroupName === DataManagerSections.SENDING_DATA).map((item: IActions, idx: number) => {
        return (
            <ToolTipWrapper
                key={idx}
                toolTipContent={{
                    title: item.Parameter,
                    toolTip: [{ label: 'Description', value: item.Description }],
                    color: 'dark',
                    iconName: 'Send',
                    iconSize: 2.5,
                    timer: 250,
                    fixedSize: true,
                }}
            >
                <ExecButton
                    weight={500}
                    key={idx}
                    textColor="dark"
                    size="m"
                    stretch
                    label={item.Label}
                    iconSize={1.8}
                    iconName={'Send'}
                    func={item.Commiting ? () => onSendingData(item) : () => onLaunchData(item)}
                />
            </ToolTipWrapper>
        );
    });
    
    const fileManagement = rootDepartment.Actions.filter((item: IActions) => item.GroupName === DataManagerSections.FILE_MANAGEMENT).map((item: IActions, idx: number) => {
        // console.log('ICON: ', `${trimString(item.Label).toLowerCase()}_data`);
        return (
            <ToolTipWrapper
                key={idx}
                toolTipContent={{
                    title: item.Parameter,
                    toolTip: [{ label: 'Description', value: item.Description }],
                    color: 'dark',
                    iconName: `${trimString(item.Label)}_data`,
                    iconSize: 2.5,
                    timer: 250,
                    fixedSize: true,
                }}
            >
                <ExecButton
                    weight={500}
                    key={idx}
                    textColor="dark"
                    size="m"
                    stretch
                    iconSize={1.8}
                    label={item.Label}
                    iconName={`${trimString(item.Label)}_data`}
                    func={item.Commiting ? () => onSendingData(item) : () => onLaunchData(item)}
                />
            </ToolTipWrapper>
        );
    });

    const folderStructure = rootDepartment.Actions.filter((item: IActions) => item.GroupName === DataManagerSections.FOLDER_STRUCTURE).map((item: IActions, idx: number) => {
        return (
            <ExecButton
                weight={500}
                key={idx}
                textColor="dark"
                size="m"
                stretch
                iconSize={1.8}
                label={item.Label}
                iconName={`${item.Parameter}_data`}
                func={item.Commiting ? () => onSendingData(item) : () => onLaunchData(item)}
            />
        );
    });

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputField(e.currentTarget.value);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Modal modalKey={"commitModal"}>
                <ModalContentWrapper title={`Version (${selectedAction.Label})`}>
                    <Spacing marginSpacing={'none'} paddingSpacing={'s'}></Spacing>
                    <FlexCenter position="start" gap={1.4}>
                        <Input placeholder={'Version message...'} value={inputField} func={(e) => onInputChange(e)} />
                        <Button stretch label={'Apply and send data'} role={'accent-orange'} size={'medium'} func={() => onLaunchData(selectedAction)} />
                    </FlexCenter>
                </ModalContentWrapper>
            </Modal>
   
            {folderStructure.length !== 0 && (
                <HideShowSection hasArrow={true} label="Folder structure">
                    {folderStructure}
                </HideShowSection>
            )}

            {sendingData.length !== 0 && (
                <HideShowSection hasArrow={true} label="Sending data">
                    {sendingData}
                </HideShowSection>
            )}

            {fileManagement.length !== 0 && (
                <HideShowSection hasArrow={true} label="File management">
                    {fileManagement}
                </HideShowSection>
            )}
        </div>
    );
};
